/**
 * FT-122b: Compliance Register Tab
 *
 * Table view showing tracked compliance items with expiry dates and traffic light status.
 * Categories: Vessel Certificates, Policies, SMS Documents
 *
 * UI Pattern: 2/3 + 1/3 layout (EXACT COPY of CrewListContent)
 * - Desktop: Main content (2/3) + Sidebar (1/3)
 * - Mobile: Stacked content + Summary box
 */

import React, { useState, useEffect } from 'react';
import { Plus, PlusCircle, Pencil, Trash2, ExternalLink, FileText, AlertCircle, CheckCircle, Clock, Download, HelpCircle, Search, ClipboardCheck, FolderOpen, Settings } from 'lucide-react';
import { useComplianceRegister, COMPLIANCE_CATEGORIES } from '../../../../hooks/domain/useComplianceRegister';
import { useSMSDocumentsConfig, FOLDER_TYPE_LABELS, FOLDER_TYPE_PLACEHOLDERS, FOLDER_TYPE_HELP, FOLDER_TYPES } from '../../../../hooks/domain/documents/useSMSDocumentsConfig';
// FT-358: middle-slot actions registered via context; the shell owns the bar.
import { useSetBottomBarActions } from '../../../../contexts/MobileBottomBarActionsContext';
import ComplianceItemModal from './ComplianceItemModal';
// FT-356: back navigation moved to shell-level MobileBottomBar via
// NavigationBackContext. Header BackButton removed.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';

// Status badge component
const StatusBadge = ({ status }) => {
  const config = {
    valid: { icon: CheckCircle, bg: 'bg-green-100', text: 'text-green-800', label: 'Valid' },
    expiring: { icon: Clock, bg: 'bg-amber-100', text: 'text-amber-800', label: 'Expiring' },
    expired: { icon: AlertCircle, bg: 'bg-red-100', text: 'text-red-800', label: 'Overdue' },
    not_set: { icon: HelpCircle, bg: 'bg-gray-100', text: 'text-gray-600', label: 'Not Set' }
  };

  const { icon: Icon, bg, text, label } = config[status] || config.not_set;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${bg} ${text}`}>
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
};

// FT-122b: Summary Card (matches CrewSummaryCard exactly)
const ComplianceSummaryCard = ({ summary }) => {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header - exact match to CrewSummaryCard */}
      <div className="bg-slate-700 px-5 py-3">
        <h3 className="text-base font-semibold text-white flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5 text-white" />
          Compliance Summary
        </h3>
      </div>

      <div className="p-6">
        {/* Status Counts - exact match to CrewSummaryCard */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm text-gray-600">Valid</span>
            </div>
            <span className="text-sm font-semibold text-green-600">{summary.valid}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
              <span className="text-sm text-gray-600">Expiring</span>
            </div>
            <span className="text-sm font-semibold text-amber-600">{summary.expiring}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              <span className="text-sm text-gray-600">Overdue</span>
            </div>
            <span className="text-sm font-semibold text-red-600">{summary.expired}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
              <span className="text-sm text-gray-600">Not Set</span>
            </div>
            <span className="text-sm font-semibold text-gray-600">{summary.notSet}</span>
          </div>
        </div>

        {/* Total - exact match to CrewSummaryCard */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Total</span>
            <span className="text-sm font-semibold text-gray-900">{summary.total} items</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// FT-122d: Documents Folder Setup Modal
const DocumentsFolderModal = ({ onClose, folderUrl, folderType, onSave }) => {
  const [selectedType, setSelectedType] = useState(folderType || FOLDER_TYPES.OTHER);
  const [url, setUrl] = useState(folderUrl || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!url.trim()) return;
    setSaving(true);
    try {
      await onSave(url.trim(), selectedType);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Set Up Documents Folder
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <span className="sr-only">Close</span>
              ×
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            <p className="text-sm text-gray-600">
              Link to your external cloud storage folder where SMS documents are kept.
            </p>

            {/* Provider Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Storage Provider
              </label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(FOLDER_TYPE_LABELS).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setSelectedType(key)}
                    className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                      selectedType === key
                        ? 'bg-cyan-50 border-cyan-600 text-cyan-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* URL Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Folder URL
              </label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={FOLDER_TYPE_PLACEHOLDERS[selectedType] || 'https://...'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                {FOLDER_TYPE_HELP[selectedType]}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!url.trim() || saving}
              className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// FT-122b: Quick Actions Card (matches CrewSidebar exactly)
// FT-122d: Added Documents Folder button
const QuickActionsCard = ({ onAddItem, folderUrl, onSetupFolder, onOpenFolder }) => {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="bg-slate-700 px-5 py-3">
        <h3 className="text-base font-semibold text-white flex items-center gap-2">
          <FileText className="h-5 w-5 text-white" />
          Quick Actions
        </h3>
      </div>
      <div className="p-6 space-y-2">
        {/* FT-16: min-h-[44px] ensures 44px+ touch targets on mobile */}
        <button
          onClick={onAddItem}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 md:py-2 text-base md:text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700 min-h-[44px]"
        >
          <Plus className="h-5 w-5 md:h-4 md:w-4" />
          Add Item
        </button>

        {/* FT-122d: Documents Folder button */}
        {folderUrl ? (
          <div className="flex gap-2">
            <button
              onClick={onOpenFolder}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 md:py-2 text-base md:text-sm font-medium text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-md hover:bg-cyan-100 min-h-[44px]"
            >
              <FolderOpen className="h-5 w-5 md:h-4 md:w-4" />
              Open Documents
            </button>
            <button
              onClick={onSetupFolder}
              className="inline-flex items-center justify-center p-3 md:p-2 text-gray-500 bg-gray-100 border border-gray-200 rounded-md hover:bg-gray-200 min-h-[44px]"
              title="Change folder settings"
            >
              <Settings className="h-5 w-5 md:h-4 md:w-4" />
            </button>
          </div>
        ) : (
          <button
            onClick={onSetupFolder}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 md:py-2 text-base md:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 min-h-[44px]"
          >
            <FolderOpen className="h-5 w-5 md:h-4 md:w-4" />
            Set Up Documents Folder
          </button>
        )}
      </div>
    </div>
  );
};

// FT-122b: Sidebar (matches CrewSidebar exactly)
// FT-122d: Added folder props
const ComplianceSidebar = ({ summary, onAddItem, folderUrl, onSetupFolder, onOpenFolder }) => {
  return (
    <div className="space-y-6">
      <ComplianceSummaryCard summary={summary} />
      <QuickActionsCard
        onAddItem={onAddItem}
        folderUrl={folderUrl}
        onSetupFolder={onSetupFolder}
        onOpenFolder={onOpenFolder}
      />
    </div>
  );
};

// Category filter buttons (pills inside content area)
// FT-122c: Renamed categories to Certificate / SMS Document / Risk Assessment
const CategoryFilter = ({ activeCategory, onCategoryChange }) => {
  const categories = [
    { id: 'all', label: 'All' },
    { id: 'certificate', label: 'Certificates' },
    { id: 'sms_document', label: 'SMS Documents' },
    { id: 'risk_assessment', label: 'Risk Assessments' }
  ];

  return (
    <div className="flex flex-wrap gap-2">
      {categories.map(cat => (
        <button
          key={cat.id}
          onClick={() => onCategoryChange(cat.id)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            activeCategory === cat.id
              ? 'bg-cyan-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {cat.label}
        </button>
      ))}
    </div>
  );
};

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Desktop table view
const DesktopTable = ({ items, onEdit, onDelete }) => {
  return (
    <div className="hidden md:block overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-slate-800">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Document</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Category</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Expiry Date</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Status</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Link</th>
            <th className="px-4 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {items.map(item => (
            <tr key={item.id} className="hover:bg-gray-50">
              <td className="px-4 py-3">
                <div className="flex items-center">
                  <FileText className="h-5 w-5 text-gray-400 mr-2" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">{item.name}</div>
                    {item.responsiblePerson && (
                      <div className="text-xs text-gray-500">{item.responsiblePerson}</div>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {COMPLIANCE_CATEGORIES[item.category] || item.category}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {formatDate(item.expiryDate)}
              </td>
              <td className="px-4 py-3">
                <StatusBadge status={item.status} />
              </td>
              <td className="px-4 py-3">
                {item.documentLink ? (
                  <a
                    href={item.documentLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-cyan-600 hover:text-cyan-800"
                    title="Open document"
                  >
                    <ExternalLink className="h-5 w-5" />
                  </a>
                ) : (
                  <span className="text-gray-300">—</span>
                )}
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => onEdit(item)}
                    className="p-1.5 text-gray-500 hover:text-cyan-600 hover:bg-gray-100 rounded"
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onDelete(item)}
                    className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-gray-100 rounded"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Mobile card view
const MobileCards = ({ items, onEdit, onDelete }) => {
  return (
    <div className="md:hidden space-y-3">
      {items.map(item => (
        <div key={item.id} className="bg-white shadow rounded-lg p-4" onClick={() => onEdit(item)}>
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <h3 className="text-sm font-medium text-gray-900">{item.name}</h3>
              <p className="text-xs text-gray-500 mt-1">
                {COMPLIANCE_CATEGORIES[item.category] || item.category}
              </p>
              <p className="text-xs text-gray-500">
                {item.expiryDate ? `Expires: ${formatDate(item.expiryDate)}` : 'No expiry set'}
              </p>
              {item.responsiblePerson && (
                <p className="text-xs text-gray-400 mt-1">{item.responsiblePerson}</p>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              <StatusBadge status={item.status} />
              {item.documentLink && (
                <a
                  href={item.documentLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyan-600"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Empty state
const EmptyState = ({ onAddItem }) => (
  <div className="bg-white shadow rounded-lg p-12 text-center">
    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
    <h3 className="text-lg font-medium text-gray-900 mb-2">No compliance items</h3>
    <p className="text-gray-500 mb-4">
      Add certificates, policies, and documents to track their expiry dates.
    </p>
    <button
      onClick={onAddItem}
      className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
    >
      <Plus className="h-5 w-5" />
      Add First Item
    </button>
  </div>
);

const ComplianceRegisterTab = ({ onBack }) => {
  const { items, addItem, updateItem, deleteItem, getByCategory, summary, refreshStatuses } = useComplianceRegister();
  const { folderUrl, folderType, setFolderLocation } = useSMSDocumentsConfig();
  // FT-356: register onBack (if any) as the shell-level back handler.
  useSetBackHandler(onBack);
  const [activeCategory, setActiveCategory] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [folderModalOpen, setFolderModalOpen] = useState(false);

  // Refresh statuses on mount (recalculate based on current date)
  useEffect(() => {
    refreshStatuses();
  }, [refreshStatuses]);

  // Filter by category then by search
  const filteredItems = getByCategory(activeCategory).filter(item => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      item.name?.toLowerCase().includes(term) ||
      item.responsiblePerson?.toLowerCase().includes(term) ||
      item.notes?.toLowerCase().includes(term)
    );
  });

  const handleAdd = () => {
    setEditingItem(null);
    setModalOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setModalOpen(true);
  };

  const handleDelete = async (item) => {
    if (window.confirm(`Delete "${item.name}"? This cannot be undone.`)) {
      await deleteItem(item.id);
    }
  };

  const handleSave = async (itemData) => {
    if (editingItem) {
      await updateItem(editingItem.id, itemData);
    } else {
      await addItem(itemData);
    }
    setModalOpen(false);
    setEditingItem(null);
  };

  // FT-122d: Folder handlers
  const handleSetupFolder = () => {
    setFolderModalOpen(true);
  };

  const handleOpenFolder = () => {
    if (folderUrl) {
      window.open(folderUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleSaveFolder = async (url, type) => {
    await setFolderLocation(url, type);
  };

  // FT-358: register middle-slot bottom-bar actions per brief §6
  // (Compliance Register: [+ Add] [Export]). Export placeholder — no
  // helper existed pre-FT-358 so we log; wire up when export lands.
  useSetBottomBarActions([
    {
      key: 'compliance-add',
      icon: PlusCircle,
      label: 'Add',
      primary: true,
      onClick: handleAdd,
    },
    {
      key: 'compliance-export',
      icon: Download,
      label: 'Export',
      onClick: () => {
        /* TODO: Compliance register export — placeholder wired FT-358 */
      },
    },
  ]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* Main Content - 2/3 width on desktop (EXACT match to CrewListContent) */}
      <div className="lg:col-span-2">
        {/* FT-356: Back navigation moved to shell-level MobileBottomBar. */}
        <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
          {/* Header - slate-700 with search inside (EXACT match to CrewListContent) */}
          <div className="bg-slate-700 px-5 py-3 rounded-t-2xl">
            <div className="flex items-center justify-between gap-4">
              <h3 className="text-base font-semibold text-white whitespace-nowrap">
                Compliance Register
              </h3>

              {/* Search Input */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search items..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-white/20 rounded-md bg-white text-slate-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>
            </div>
          </div>

          {/* Category filters - below header */}
          <div className="px-6 pt-4">
            <CategoryFilter activeCategory={activeCategory} onCategoryChange={setActiveCategory} />
          </div>

          {/* Content - add bottom padding for mobile bottom bar */}
          <div className="p-6 pb-24 lg:pb-6">
            {filteredItems.length === 0 ? (
              <EmptyState onAddItem={handleAdd} />
            ) : (
              <>
                <DesktopTable items={filteredItems} onEdit={handleEdit} onDelete={handleDelete} />
                <MobileCards items={filteredItems} onEdit={handleEdit} onDelete={handleDelete} />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Sidebar - 1/3 width - Hidden on mobile (EXACT match to CrewListContent) */}
      <div className="hidden lg:block">
        <ComplianceSidebar
          summary={summary}
          onAddItem={handleAdd}
          folderUrl={folderUrl}
          onSetupFolder={handleSetupFolder}
          onOpenFolder={handleOpenFolder}
        />
      </div>

      {/* Mobile Summary Box - ONLY visible on mobile (<lg) */}
      <div className="lg:hidden mb-20 px-4">
        <ComplianceSummaryCard summary={summary} />
      </div>

      {/* FT-358: mobile bottom-bar actions registered via
          useSetBottomBarActions above — the shell owns the bar. */}

      {/* Add/Edit Modal */}
      {modalOpen && (
        <ComplianceItemModal
          item={editingItem}
          onSave={handleSave}
          onClose={() => {
            setModalOpen(false);
            setEditingItem(null);
          }}
        />
      )}

      {/* FT-122d: Documents Folder Setup Modal */}
      {folderModalOpen && (
        <DocumentsFolderModal
          onClose={() => setFolderModalOpen(false)}
          folderUrl={folderUrl}
          folderType={folderType}
          onSave={handleSaveFolder}
        />
      )}
    </div>
  );
};

export default ComplianceRegisterTab;
